"use client";

import { c as _c } from "react/compiler-runtime";
import { EyeIcon, EyeOffIcon, PencilIcon, PlusIcon, TrashIcon, WrenchIcon } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useI18n } from "@llmgateway/i18n/react";
import type { McpServer } from "@/hooks/useMcpServers";
interface McpServersDialogProps {
  servers: McpServer[];
  onAddServer: (server: Omit<McpServer, "id">) => McpServer;
  onUpdateServer: (id: string, updates: Partial<Omit<McpServer, "id">>) => void;
  onRemoveServer: (id: string) => void;
  onToggleServer: (id: string) => void;
}
export function McpServersDialog(t0) {
  const $ = _c(82);
  const {
    servers,
    onAddServer,
    onUpdateServer,
    onRemoveServer,
    onToggleServer
  } = t0;
  const {
    t
  } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [showApiKey, setShowApiKey] = useState(t1);
  const [formName, setFormName] = useState("");
  const [formUrl, setFormUrl] = useState("");
  const [formApiKey, setFormApiKey] = useState("");
  let t2;
  if ($[1] !== servers) {
    t2 = servers.filter(_temp);
    $[1] = servers;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const enabledCount = t2.length;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setFormName("");
      setFormUrl("");
      setFormApiKey("");
      setIsAddingNew(false);
      setEditingId(null);
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const resetForm = t3;
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      resetForm();
      setIsAddingNew(true);
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleStartAdd = t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = server => {
      setFormName(server.name);
      setFormUrl(server.url);
      setFormApiKey(server.apiKey);
      setEditingId(server.id);
      setIsAddingNew(false);
    };
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const handleStartEdit = t5;
  let t6;
  if ($[6] !== editingId || $[7] !== formApiKey || $[8] !== formName || $[9] !== formUrl || $[10] !== onAddServer || $[11] !== onUpdateServer) {
    t6 = () => {
      if (!formName.trim() || !formUrl.trim()) {
        return;
      }
      if (editingId) {
        onUpdateServer(editingId, {
          name: formName.trim(),
          url: formUrl.trim(),
          apiKey: formApiKey.trim()
        });
      } else {
        onAddServer({
          name: formName.trim(),
          url: formUrl.trim(),
          apiKey: formApiKey.trim(),
          enabled: true
        });
      }
      resetForm();
    };
    $[6] = editingId;
    $[7] = formApiKey;
    $[8] = formName;
    $[9] = formUrl;
    $[10] = onAddServer;
    $[11] = onUpdateServer;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleSave = t6;
  let t7;
  if ($[13] !== editingId || $[14] !== onRemoveServer) {
    t7 = id => {
      onRemoveServer(id);
      if (editingId === id) {
        resetForm();
      }
    };
    $[13] = editingId;
    $[14] = onRemoveServer;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleDelete = t7;
  let t8;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = id_0 => {
      setShowApiKey(prev => ({
        ...prev,
        [id_0]: !prev[id_0]
      }));
    };
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const toggleApiKeyVisibility = t8;
  let t9;
  if ($[17] !== t) {
    t9 = t("mcp.title");
    $[17] = t;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <WrenchIcon className="h-4 w-4" />;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== enabledCount) {
    t11 = enabledCount > 0 && <span className="bg-primary text-primary-foreground absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-medium">{enabledCount}</span>;
    $[20] = enabledCount;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t9) {
    t12 = <TooltipTrigger asChild={true}><DialogTrigger asChild={true}><Button variant="ghost" size="icon" className="relative h-8 w-8" aria-label={t9}>{t10}{t11}</Button></DialogTrigger></TooltipTrigger>;
    $[22] = t11;
    $[23] = t9;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== enabledCount || $[26] !== t) {
    t13 = t("mcp.activeCount", {
      count: enabledCount
    });
    $[25] = enabledCount;
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t13) {
    t14 = <TooltipContent><p>{t13}</p></TooltipContent>;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t12 || $[31] !== t14) {
    t15 = <Tooltip>{t12}{t14}</Tooltip>;
    $[30] = t12;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== t) {
    t16 = t("mcp.title");
    $[33] = t;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t16) {
    t17 = <DialogTitle>{t16}</DialogTitle>;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t) {
    t18 = t("mcp.description");
    $[37] = t;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  let t19;
  if ($[39] !== t18) {
    t19 = <DialogDescription>{t18}</DialogDescription>;
    $[39] = t18;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== t17 || $[42] !== t19) {
    t20 = <DialogHeader>{t17}{t19}</DialogHeader>;
    $[41] = t17;
    $[42] = t19;
    $[43] = t20;
  } else {
    t20 = $[43];
  }
  let t21;
  if ($[44] !== editingId || $[45] !== handleDelete || $[46] !== isAddingNew || $[47] !== onToggleServer || $[48] !== servers || $[49] !== t) {
    t21 = servers.length > 0 && !isAddingNew && !editingId && <div className="space-y-2">{servers.map(server_0 => <div key={server_0.id} className="bg-muted/50 flex items-center justify-between rounded-lg border p-3"><div className="min-w-0 flex-1"><div className="flex items-center gap-2"><span className="font-medium">{server_0.name}</span><Switch checked={server_0.enabled} onCheckedChange={() => onToggleServer(server_0.id)} className="scale-75" /></div><p className="text-muted-foreground truncate text-xs">{server_0.url}</p></div><div className="flex items-center gap-1"><Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleStartEdit(server_0)} aria-label={t("mcp.editServer")} title={t("mcp.editServer")}><PencilIcon className="h-3.5 w-3.5" /></Button><Button variant="ghost" size="icon" className="text-destructive h-7 w-7" onClick={() => handleDelete(server_0.id)} aria-label={t("mcp.deleteServer")} title={t("mcp.deleteServer")}><TrashIcon className="h-3.5 w-3.5" /></Button></div></div>)}</div>;
    $[44] = editingId;
    $[45] = handleDelete;
    $[46] = isAddingNew;
    $[47] = onToggleServer;
    $[48] = servers;
    $[49] = t;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== editingId || $[52] !== formApiKey || $[53] !== formName || $[54] !== formUrl || $[55] !== handleSave || $[56] !== isAddingNew || $[57] !== showApiKey || $[58] !== t) {
    t22 = (isAddingNew || editingId) && <div className="space-y-4 rounded-lg border p-4"><div className="space-y-2"><Label htmlFor="mcp-name">{t("mcp.name")}</Label><Input id="mcp-name" placeholder="My MCP Server" value={formName} onChange={e => setFormName(e.target.value)} /></div><div className="space-y-2"><Label htmlFor="mcp-url">{t("mcp.url")}</Label><Input id="mcp-url" placeholder="https://api.example.com/mcp" value={formUrl} onChange={e_0 => setFormUrl(e_0.target.value)} /></div><div className="space-y-2"><Label htmlFor="mcp-api-key">{t("mcp.apiKeyOptional")}</Label><div className="relative"><Input id="mcp-api-key" type={showApiKey.form ? "text" : "password"} placeholder="sk-..." value={formApiKey} onChange={e_1 => setFormApiKey(e_1.target.value)} className="pr-10" /><Button type="button" variant="ghost" size="icon" className="absolute top-1/2 right-1 h-7 w-7 -translate-y-1/2" onClick={() => toggleApiKeyVisibility("form")} aria-label={showApiKey.form ? t("mcp.hideApiKey") : t("mcp.showApiKey")} title={showApiKey.form ? t("mcp.hideApiKey") : t("mcp.showApiKey")}>{showApiKey.form ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}</Button></div></div><div className="flex justify-end gap-2"><Button variant="outline" size="sm" onClick={resetForm}>{t("common.cancel")}</Button><Button size="sm" onClick={handleSave} disabled={!formName.trim() || !formUrl.trim()}>{editingId ? t("mcp.save") : t("mcp.addServer")}</Button></div></div>;
    $[51] = editingId;
    $[52] = formApiKey;
    $[53] = formName;
    $[54] = formUrl;
    $[55] = handleSave;
    $[56] = isAddingNew;
    $[57] = showApiKey;
    $[58] = t;
    $[59] = t22;
  } else {
    t22 = $[59];
  }
  let t23;
  if ($[60] !== isAddingNew || $[61] !== servers.length || $[62] !== t) {
    t23 = servers.length === 0 && !isAddingNew && <div className="text-muted-foreground py-8 text-center text-sm"><WrenchIcon className="mx-auto mb-2 h-8 w-8 opacity-50" /><p>{t("mcp.emptyTitle")}</p><p className="mt-1 text-xs">{t("mcp.emptyDescription")}</p></div>;
    $[60] = isAddingNew;
    $[61] = servers.length;
    $[62] = t;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== t21 || $[65] !== t22 || $[66] !== t23) {
    t24 = <div className="max-h-[400px] space-y-4 overflow-y-auto py-4">{t21}{t22}{t23}</div>;
    $[64] = t21;
    $[65] = t22;
    $[66] = t23;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== editingId || $[69] !== isAddingNew || $[70] !== t) {
    t25 = !isAddingNew && !editingId && <Button onClick={handleStartAdd} className="gap-2"><PlusIcon className="h-4 w-4" />{t("mcp.addServer")}</Button>;
    $[68] = editingId;
    $[69] = isAddingNew;
    $[70] = t;
    $[71] = t25;
  } else {
    t25 = $[71];
  }
  let t26;
  if ($[72] !== t25) {
    t26 = <DialogFooter>{t25}</DialogFooter>;
    $[72] = t25;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  let t27;
  if ($[74] !== t20 || $[75] !== t24 || $[76] !== t26) {
    t27 = <DialogContent className="sm:max-w-[500px]">{t20}{t24}{t26}</DialogContent>;
    $[74] = t20;
    $[75] = t24;
    $[76] = t26;
    $[77] = t27;
  } else {
    t27 = $[77];
  }
  let t28;
  if ($[78] !== isOpen || $[79] !== t15 || $[80] !== t27) {
    t28 = <Dialog open={isOpen} onOpenChange={setIsOpen}>{t15}{t27}</Dialog>;
    $[78] = isOpen;
    $[79] = t15;
    $[80] = t27;
    $[81] = t28;
  } else {
    t28 = $[81];
  }
  return t28;
}
function _temp(s) {
  return s.enabled;
}